/**
 * API Error Handler Utility
 * Provides consistent error handling across the application
 */

// URL validation function to prevent undefined concatenations
export const validateApiUrl = (url, endpoint = '') => {
  // Check for undefined values in the URL
  if (url && url.includes('undefined')) {
    console.error('Malformed URL detected with undefined value:', url);
    return false;
  }
  
  // Check if endpoint contains undefined
  if (endpoint && endpoint.includes('undefined')) {
    console.error('Malformed endpoint detected with undefined value:', endpoint);
    return false;
  }
  
  // Basic URL format validation
  try {
    const fullUrl = url + (endpoint || '');
    new URL(fullUrl);
    return true;
  } catch (e) {
    console.error('Invalid URL format:', url + (endpoint || ''), e);
    return false;
  }
};

// Enhanced error handler with URL validation
export const handleApiError = (error, context = 'API call', showToast = null) => {
  // Log the error for debugging
  console.error(`${context} error:`, error);

  // Check for malformed URL errors
  if (error.config && error.config.url && error.config.url.includes('undefined')) {
    console.error('Malformed URL detected in API call:', error.config.url);
    const errorMessage = 'Invalid request URL. Please refresh the page and try again.';
    
    if (showToast) {
      showToast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    
    return {
      message: errorMessage,
      shouldShowToast: true,
      isNetworkError: false,
      isMalformedUrl: true
    };
  }

  // Determine error type and message
  let errorMessage = 'An unexpected error occurred';
  let shouldShowToast = false;

  if (error.code === 'ERR_NETWORK' || error.code === 'ERR_NAME_NOT_RESOLVED') {
    // Network connectivity issues - common in development
    errorMessage = 'Unable to connect to server. Please check your connection.';
    shouldShowToast = false; // Don't spam user with network errors in development
  } else if (error.response) {
    // Server responded with error status
    const status = error.response.status;
    
    switch (status) {
      case 401:
        errorMessage = 'Authentication required. Please log in again.';
        shouldShowToast = true;
        // Clear token and redirect to login
        localStorage.removeItem('tocken');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        break;
      case 403:
        errorMessage = 'Access denied. You do not have permission for this action.';
        shouldShowToast = true;
        break;
      case 404:
        errorMessage = 'The requested resource was not found.';
        shouldShowToast = true;
        break;
      case 500:
        errorMessage = 'Server error. Please try again later.';
        shouldShowToast = true;
        break;
      default:
        errorMessage = error.response.data?.message || 'Server error occurred';
        shouldShowToast = true;
    }
  } else if (error.request) {
    // Request was made but no response received
    errorMessage = 'No response from server. Please try again.';
    shouldShowToast = false; // Likely network issue
  }

  // Show toast if requested and appropriate
  if (showToast && shouldShowToast) {
    showToast({
      title: 'Error',
      description: errorMessage,
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  }

  return {
    message: errorMessage,
    shouldShowToast,
    isNetworkError: error.code === 'ERR_NETWORK' || error.code === 'ERR_NAME_NOT_RESOLVED'
  };
};

/**
 * Creates a safe API call wrapper that handles errors gracefully
 */
export const safeApiCall = async (apiCall, fallbackData = null, context = 'API call', showToast = null, endpoint = '') => {
  try {
    // Pre-validate URL if endpoint is provided
    if (endpoint) {
      // Get the base URL from the API instance (assuming it's available)
      const baseUrl = 'https://back.printster.in/api/';
      if (!validateApiUrl(baseUrl, endpoint)) {
        throw new Error(`Malformed URL: ${baseUrl}${endpoint}`);
      }
    }
    
    const response = await apiCall();
    return {
      success: true,
      data: response.data,
      response
    };
  } catch (error) {
    // Check if this is a URL validation error
    if (error.message && error.message.includes('Malformed URL')) {
      console.error('URL validation failed:', error.message);
      return {
        success: false,
        error: {
          type: 'malformedUrl',
          message: 'Invalid request URL. Please refresh the page and try again.',
          isExpected: false
        },
        data: fallbackData
      };
    }
    
    const errorInfo = handleApiError(error, context, showToast);
    
    // For network errors in development, provide fallback data
    if (errorInfo.isExpected && fallbackData !== null) {
      console.log(`Using fallback data for ${context}`);
      return {
        success: true,
        data: fallbackData,
        isFallback: true
      };
    }
    
    return { 
      success: false, 
      data: fallbackData, 
      error: errorInfo 
    };
  }
};

const apiErrorHandler = { handleApiError, safeApiCall, validateApiUrl };
export default apiErrorHandler;